package com.tregz.tremenz.ui.page

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.tregz.polyglot.di.language
import com.tregz.tremenz.ui.component.Title
import com.tregz.tremenz.ui.label.LabelKey
import com.tregz.tremenz.ui.label.labelText

/** The crawlable twin of this page. It carries the same copy as plain HTML for search and AI bots. */
const val ABOUT_URL = "https://tremenz.com/about.html"

/** French twin of [ABOUT_URL], served under `/fr/`. */
const val ABOUT_URL_FR = "https://tremenz.com/fr/about.html"

/** The static page in the visitor's language: French for French speakers, English for everyone else. */
fun aboutUrl(languageCode: String = language.code): String =
    if (languageCode == "fr") ABOUT_URL_FR else ABOUT_URL

/**
 * Web exclusive "About" tab: what Tremenz is, what it plays and how to listen.
 *
 * The copy mirrors `about.html` so what visitors read here is what search engines index.
 */
@Composable
fun PageAbout(
    onOpenLink: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    val scheme = MaterialTheme.colorScheme
    val sections = listOf(
        LabelKey.ABOUT_WHAT_TITLE to LabelKey.ABOUT_WHAT_BODY,
        LabelKey.ABOUT_LISTEN_TITLE to LabelKey.ABOUT_LISTEN_BODY,
        LabelKey.ABOUT_STYLES_TITLE to LabelKey.ABOUT_STYLES_BODY,
        LabelKey.ABOUT_WHO_TITLE to LabelKey.ABOUT_WHO_BODY
    )

    Column(
        modifier = modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
    ) {
        Title(
            title = labelText(LabelKey.ROUTE_ABOUT),
            subtitle = labelText(LabelKey.HEADING_ABOUT_SUBTITLE)
        )

        sections.forEach { (title, body) ->
            Card(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp, vertical = 6.dp),
                colors = CardDefaults.cardColors(containerColor = scheme.surface),
                shape = MaterialTheme.shapes.medium
            ) {
                Column(modifier = Modifier.padding(16.dp)) {
                    Text(
                        text = labelText(title),
                        color = scheme.onSurface,
                        style = MaterialTheme.typography.titleMedium
                    )
                    Spacer(modifier = Modifier.height(8.dp))
                    Text(
                        text = labelText(body),
                        color = scheme.onSurfaceVariant,
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
            }
        }

        TextButton(
            onClick = { onOpenLink(aboutUrl()) },
            modifier = Modifier.padding(horizontal = 8.dp, vertical = 8.dp)
        ) {
            Text(text = labelText(LabelKey.ABOUT_MORE), color = scheme.primary)
        }
        Spacer(modifier = Modifier.height(16.dp))
    }
}
